import { NgClass, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { PayLaterInfoComponent } from '@components/pay-later-info/pay-later-info.component';
import { environment } from '@environments/environment';
import { InHomeCollectionService, ResultService } from '@Medology/ng-findalab';
import {
  CreditCardPayment,
  FormLayout,
  GiftCardPayment,
  NgxPaymentFormComponent,
  Payment,
  PaymentTypes,
} from '@medology/ngx-payment';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';

@Component({
  selector: 'app-payment-step-tabs',
  templateUrl: './payment-step-tabs.component.html',
  styleUrls: ['./payment-step-tabs.component.scss'],
  imports: [NgIf, NgClass, PayLaterInfoComponent, NgxPaymentFormComponent],
})
export class PaymentStepTabsComponent implements OnChanges, OnInit {
  @Input({ required: true }) public hasPartner: boolean;
  @Input({ required: true }) public totalAmount: number;
  @Input() public selectedPaymentType: PaymentTypes = PaymentTypes.CreditCard;
  @Input() public enablePayLater: boolean = true;

  @Output() public paymentsSelected = new EventEmitter<Payment[]>();
  @Output() public formValid = new EventEmitter<void>();

  @ViewChild('ngxPaymentForm', { static: false }) public ngxPaymentFormComponent: NgxPaymentFormComponent;

  public isInHomeCollection: boolean = false;
  public isPayNowTabSelected: boolean = true;
  public ignoredPaymentTypes: PaymentTypes[] = [];
  public orderService = inject(OrderService);

  private inHomeService = inject(InHomeCollectionService);
  private resultService = inject(ResultService);
  private storageService = inject(StorageService);
  private consultationRequestService = inject(ConsultationRequestService);

  private payments: Payment[] = [];

  /**
   * Gets whether pay later is enabled.
   */
  public get isPayLaterEnabled(): boolean {
    return environment.enablePayLater && !this.isInHomeCollection && this.enablePayLater;
  }

  /**
   * Gets the form layout configuration for the payment form.
   */
  public get orderPaymentFormLayout(): FormLayout {
    return environment.order.orderPaymentFormLayout as FormLayout;
  }

  /**
   * Gets whether the Gift Card payment option must be hidden for the current order.
   */
  public get isGiftCardDisabled(): boolean {
    return this.hasPartner || this.hasSubscriptionConsultationOrder();
  }

  /**
   * Set payment form, which will be used in the PaymentInfoComponent child
   * component.
   */
  public ngOnInit(): void {
    this.payNowValidationOnInHomeCollection();
  }

  /**
   * Handle component changes.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.hasPartner) {
      return;
    }

    this.ignoredPaymentTypes = this.isGiftCardDisabled ? [PaymentTypes.GiftCard] : [];

    if (this.hasPartner && !this.isPayNowTabSelected) {
      this.setPayNow(true);
    }

    this.syncPaymentFormOnPartnerToggle();
  }

  /**
   * Validates the current payment form by delegating to the NgxPaymentFormComponent.
   *
   * @returns {boolean} whether the payment form is valid
   */
  public validatePaymentForm(): boolean {
    return this.ngxPaymentFormComponent?.validateForm() ?? false;
  }

  /**
   * Returns the list of payment types available in the current NgxPaymentFormComponent instance.
   *
   * @returns {PaymentTypes[]} the available payment types, or an empty array if the form is not yet initialized
   */
  public getAvailablePaymentTypes(): PaymentTypes[] {
    return this.ngxPaymentFormComponent?.availablePaymentTypes() ?? [];
  }

  /**
   * Updates the payment method to either 'Pay Now' or 'Pay Later'.
   *
   * @param {boolean} isPayNow whether the user is paying now or later
   */
  public setPayNow(isPayNow: boolean): void {
    this.isPayNowTabSelected = isPayNow;

    if (!isPayNow) {
      this.onPaymentsSelected([]);
    }
  }

  /**
   * Emits the selected payments.
   *
   * @param {Payment[]} payments the array of Payment objects that were selected
   */
  public onPaymentsSelected(payments: Payment[]): void {
    this.payments = payments;
    this.paymentsSelected.emit(payments);
  }

  /**
   * Returns true when one of the payment methods are gift card
   */
  public isGiftCardApplied(): boolean {
    return this.payments.some((payment) => payment instanceof GiftCardPayment);
  }

  /**
   * Shows the payment form and forces it to re-initialize unless the user is already on the Pay Now tab and the
   * selected payment method is Credit Card or there are no payment methods selected.
   */
  public resetPaymentFormToPayNow(): void {
    const selectedPaymentType = this.ngxPaymentFormComponent?.selectedPaymentType() ?? this.selectedPaymentType;

    if (
      this.isPayNowTabSelected &&
      (!selectedPaymentType || selectedPaymentType === PaymentTypes.CreditCard) &&
      (!this.payments.length || (this.payments.length === 1 && this.payments[0] instanceof CreditCardPayment))
    ) {
      return;
    }

    this.selectedPaymentType = PaymentTypes.CreditCard;
    this.isPayNowTabSelected = false;
    setTimeout(() => {
      this.onPaymentsSelected([]);
      this.isPayNowTabSelected = true;
    });
  }

  /**
   * Returns true when the associated consultation order belongs to a subscription.
   */
  private hasSubscriptionConsultationOrder(): boolean {
    return this.consultationRequestService.isExternalSubscription;
  }

  /**
   * Pay now validations triggered on different in home collections statuses.
   */
  private payNowValidationOnInHomeCollection(): void {
    this.isInHomeCollection = this.storageService.hasInHomeCollection;
    this.resultService.results$.subscribe(() => (this.isInHomeCollection = false));
    this.inHomeService.scheduling$.subscribe((status: boolean) => {
      this.isInHomeCollection = status;
      if (status) {
        this.setPayNow(true);
      }
    });
  }

  /**
   * Keeps the payment form in sync when the partner-testing state changes.
   */
  private syncPaymentFormOnPartnerToggle(): void {
    if (!this.ngxPaymentFormComponent) {
      return;
    }

    this.resetPaymentFormToPayNow();
  }
}
